/** @jsxImportSource preact */
import { format, parseISO } from "date-fns";
import { ok } from "neverthrow";
import { enUS } from "date-fns/locale";
import { createContext, type ComponentChildren } from "preact";
import { useContext, useEffect, useId, useRef, useState } from "preact/hooks";
import {
  supportRequestFieldsSchema,
  type SupportRequestCard,
  type SupportRequestFields,
  type SupportRequestFailure,
} from "@go-novus/admin-api";
import type {
  Conversation,
  ConversationState,
} from "../../conversation/conversation";
import * as m from "../../paraglide/messages.js";
import { ContactCard, type WidgetContactRequestType } from "./contact-card";
import { REQUEST_TYPE_LABELS } from "./contact-card";
import { StatusCard } from "../cards/status-card";
import { RequestOverlay } from "./request-overlay";
import { RequestStatus } from "./request-status";
import { PanelSheetHostContext } from "../sheet/panel-sheet";
import { ContactUnavailable } from "./contact-unavailable";
import { useSupportFeedback } from "./support-feedback";
import { useCopy } from "../use-copy";
import { requestTypes } from "./request-type";
import { ReviewCard } from "./review-card";
import { EmailResultCard } from "./email-result-card";
import { FormButton } from "../form/form-button";
import type { WidgetTokens } from "../theme";

function wireRequestType(
  type: WidgetContactRequestType,
): SupportRequestFields["request_type"] {
  const entry = Object.entries(requestTypes).find(
    ([, value]) => value === type,
  );
  if (!entry) throw new Error("Unknown request type");
  return supportRequestFieldsSchema.shape.request_type.parse(entry[0]);
}

interface DraftActions {
  operate: Conversation["operateSupportRequest"];
  edit: Conversation["editSupportRequest"];
  edits: ConversationState["supportEdits"];
  open?: Conversation["openSupportRequest"];
  chatBusy?: boolean;
  canSend: boolean;
}
export const SupportRequestContext = createContext<DraftActions | undefined>(
  undefined,
);

/** 浮层内容占满可用宽度，字段随聊天窗一起伸缩。 */
const ROOT_STYLE = { width: "100%" };

/** 错误附带的是本次服务端读取的卡，旧快照不构成未发送证据。 */
function emailUnavailableUnsent(failure: SupportRequestFailure | undefined) {
  const card = failure?.card;
  return (
    !!failure &&
    ["support_email_unavailable", "support_email_not_configured"].includes(
      failure.error,
    ) &&
    card?.lifecycle === "active" &&
    ["draft", "failed"].includes(card.payload.send_status ?? "draft") &&
    !card.payload.accepted_at
  );
}

/** 本次打开直接使用 BFF 返回的卡；重新打开快照仍先校验，编辑区由会话持有。 */
export function SupportRequestCardView({
  card,
  opening,
  tokens,
  onDismiss = () => {},
}: {
  onDismiss?: () => void;
  card?: SupportRequestCard;
  opening?: NonNullable<
    ConversationState["messages"][number]["supportOpening"]
  > & {
    messageId: string;
  };
  tokens: WidgetTokens;
}) {
  const actions = useContext(SupportRequestContext);
  const host = useContext(PanelSheetHostContext);
  const titleId = `gn-request-${useId()}`;
  const sentInOverlay = useRef(false);
  const [ready, setReady] = useState(false);
  const awaitingOpen = useRef(!card);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<SupportRequestFailure>();
  const [review, setReview] = useState(false);
  const [editingFailed, setEditingFailed] = useState(false);
  const [failedOperation, setFailedOperation] = useState<
    "read" | "save" | "pause" | "resume" | "send"
  >("read");
  const [confirmedUnsent, setConfirmedUnsent] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [, copy] = useCopy({ repeat: true });
  const feedback = useSupportFeedback();
  const busyRef = useRef(false);
  const currentCard = useRef(card);
  currentCard.current = card;
  const root = useRef<HTMLDivElement>(null);
  const requestId = card?.payload.request_id;
  const operate = actions?.operate;
  const present = (
    content: ComponentChildren,
    title?: string,
    statusTitle?: string,
  ) =>
    actions ? (
      <RequestOverlay
        tokens={tokens}
        background={host?.background ?? tokens.surfaceBg}
        touch={host?.touch ?? false}
        title={title}
        titleId={titleId}
        // 只锁本浮层自己发出的发送调用；快照里的 sending 可能要等服务端对账很久，收起不影响发送。
        locked={busy && failedOperation === "send"}
        onDismiss={onDismiss}
      >
        {statusTitle && (
          <span hidden id={titleId}>
            {statusTitle}
          </span>
        )}
        {content}
      </RequestOverlay>
    ) : (
      content
    );
  const progress = (kind: "loading" | "load_failed", retry?: () => void) =>
    present(
      <>
        {failure && (
          <p role="alert">
            {failure.error === "rate_limited"
              ? m.widget_support_rate_limited()
              : m.widget_support_read_failed()}
          </p>
        )}
        <RequestStatus
          tokens={tokens}
          titleId={titleId}
          state={{ kind }}
          onRetry={retry}
        />
      </>,
    );
  const read = async (useOpenedCard = false) => {
    if (!operate || !card || !requestId) return;
    const result = useOpenedCard
      ? ok(card)
      : await operate({ operation: "read", request_id: requestId });
    if (
      result.isOk() &&
      result.value.lifecycle === "active" &&
      result.value.payload.phase === "paused" &&
      (result.value.payload.send_status ?? "draft") === "draft"
    ) {
      return {
        operation: "resume" as const,
        result: await operate({
          operation: "resume",
          request_id: requestId,
          content_version:
            actions.edits?.[requestId]?.version ??
            result.value.payload.content_version,
        }),
      };
    }
    return { operation: "read" as const, result };
  };
  const applyRead = (loaded: Awaited<ReturnType<typeof read>>) => {
    if (!loaded) return;
    const { result, operation } = loaded;
    const unsent = result.isErr() && emailUnavailableUnsent(result.error);
    setFailedOperation(operation);
    setReady(result.isOk() || unsent || operation === "resume");
    setConfirmedUnsent(unsent);
    setFailure(result.isErr() ? result.error : undefined);
  };
  const load = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    applyRead(await read());
    setBusy(false);
    busyRef.current = false;
  };
  useEffect(() => {
    if (!card || opening?.pending) return;
    let current = true;
    busyRef.current = true;
    const useOpenedCard = awaitingOpen.current && !opening?.error;
    awaitingOpen.current = false;
    void read(useOpenedCard).then((loaded) => {
      if (!current) return;
      applyRead(loaded);
      busyRef.current = false;
    });
    return () => {
      current = false;
    };
  }, [operate, requestId, opening?.pending, opening?.error]);
  useEffect(() => {
    if (card?.lifecycle !== "active" || card.payload.send_status !== "failed")
      setEditingFailed(false);
  }, [
    card?.lifecycle,
    card?.lifecycle === "active" ? card.payload.send_status : undefined,
  ]);
  useEffect(() => {
    if (failure?.fields?.length)
      root.current
        ?.querySelector<HTMLElement>('[aria-invalid="true"]')
        ?.focus();
  }, [failure]);
  if (!card || !requestId) {
    const error = opening?.error;
    const label =
      error === "expired"
        ? m.widget_support_expired_title()
        : error === "not_accessible"
          ? m.widget_support_unavailable_title()
          : error === "support_email_unavailable" ||
              error === "support_email_not_configured"
            ? m.widget_support_open_unavailable_title()
            : undefined;
    return present(
      opening ? (
        <SupportOpenStatus
          titleId={titleId}
          messageId={opening.messageId}
          tokens={tokens}
          opening={opening}
        />
      ) : null,
      undefined,
      label,
    );
  }
  if (
    card.lifecycle !== "active" ||
    failure?.error === "not_accessible" ||
    failure?.error === "expired"
  )
    return present(
      <StatusCard
        tokens={tokens}
        live={!!actions}
        icon="lock"
        title={
          card.lifecycle === "expired" || failure?.error === "expired"
            ? m.widget_support_expired_title()
            : m.widget_support_unavailable_title()
        }
        eyebrow={m.widget_support_reference({ id: requestId })}
        description={
          card.lifecycle === "expired" || failure?.error === "expired"
            ? m.widget_support_expired()
            : m.widget_support_inaccessible()
        }
      />,
      undefined,
      card.lifecycle === "expired" || failure?.error === "expired"
        ? m.widget_support_expired_title()
        : m.widget_support_unavailable_title(),
    );
  const status = card.payload.send_status ?? "draft";
  const result =
    status !== "draft"
      ? {
          requestId,
          requestType: card.payload.request_type,
          contactEmail: card.payload.contact_email,
          to: card.payload.to,
          time: format(parseISO(card.updated_at), "PPp", { locale: enUS }),
          status,
          acceptedAt: card.payload.accepted_at
            ? format(parseISO(card.payload.accepted_at), "PPp", {
                locale: enUS,
              })
            : undefined,
        }
      : undefined;
  if (!actions)
    return result ? (
      <EmailResultCard tokens={tokens} result={result} />
    ) : (
      <p>{m.widget_support_reference({ id: requestId })}</p>
    );
  if (!ready)
    return progress(
      failure ? "load_failed" : "loading",
      failure && !busy
        ? () => {
            void load();
          }
        : undefined,
    );
  const edit = actions.edits?.[requestId];
  const fields =
    edit?.fields ?? supportRequestFieldsSchema.strip().parse(card.payload);
  const version = edit?.version ?? card.payload.content_version;
  const change = (patch: Partial<SupportRequestFields>) => {
    actions.edit(requestId, version, { ...fields, ...patch });
    setConfirmedUnsent(false);
    setCopyFailed(false);
    setReview(false);
  };
  const submit = async (operation: "save" | "pause" | "resume") => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    const result = await actions.operate(
      operation === "resume"
        ? { operation, request_id: requestId, content_version: version }
        : {
            operation,
            request_id: requestId,
            content_version: version,
            fields,
          },
    );
    setBusy(false);
    setFailure(result.isErr() ? result.error : undefined);
    busyRef.current = false;
    setFailedOperation(operation);
    if (result.isOk()) {
      if (operation === "pause") {
        setReview(false);
        onDismiss();
      }
      if (operation === "save") feedback?.show(m.widget_support_saved());
    } else {
      if (["invalid_fields", "stale_version"].includes(result.error.error))
        setReview(false);
      if (emailUnavailableUnsent(result.error)) setConfirmedUnsent(true);
      feedback?.show(
        operation === "pause"
          ? m.widget_support_pause_failed()
          : operation === "resume"
            ? m.widget_support_resume_failed()
            : result.error.error === "stale_version"
              ? m.widget_support_conflict()
              : m.widget_support_failed(),
        "danger",
      );
    }
  };
  const send = async () => {
    if (busyRef.current || !actions.canSend) return;
    // 新输入先保存并展示真实邮件的新版本，不能拿旧邮件的点击继续发送。
    if (edit) {
      await submit("save");
      return;
    }
    busyRef.current = true;
    setFailedOperation("send");
    setBusy(true);
    sentInOverlay.current = true;
    const result = await actions.operate({
      operation: "send",
      request_id: requestId,
      content_version: card.payload.content_version,
    });
    setFailedOperation("send");
    setFailure(result.isErr() ? result.error : undefined);
    if (result.isErr()) {
      if (["invalid_fields", "stale_version"].includes(result.error.error))
        setReview(false);
      if (
        ["support_email_unavailable", "support_email_not_configured"].includes(
          result.error.error,
        )
      ) {
        if (result.error.error === "support_email_not_configured") {
          // 发信服务未配置时 BFF 在发送前就拒绝，读取也会同样报错拿不到卡；这个错误本身就是未发送的结论。
          setConfirmedUnsent(true);
        } else {
          // 发送尝试之后必须读原请求，超时不能当作未发送证据。
          const checked = await actions.operate({
            operation: "read",
            request_id: requestId,
          });
          setConfirmedUnsent(
            checked.isOk() &&
              checked.value.lifecycle === "active" &&
              ["draft", "failed"].includes(
                checked.value.payload.send_status ?? "draft",
              ) &&
              !checked.value.payload.accepted_at,
          );
        }
      }
      feedback?.show(m.widget_support_send_failed(), "danger");
    }
    setBusy(false);
    busyRef.current = false;
  };
  const failureText =
    failure?.error === "stale_version"
      ? m.widget_support_conflict()
      : failure?.error === "rate_limited"
        ? m.widget_support_rate_limited()
        : failedOperation === "read"
          ? m.widget_support_read_failed()
          : failedOperation === "pause"
            ? m.widget_support_pause_failed()
            : failedOperation === "resume"
              ? m.widget_support_resume_failed()
              : failedOperation === "send"
                ? m.widget_support_send_failed()
                : m.widget_support_failed();
  // 表单与预览共用浮层标题，状态页由自身标题给对话框命名。
  const wrap = (content: ComponentChildren, showFailure = true) =>
    present(
      <div ref={root} aria-busy={busy} style={ROOT_STYLE}>
        {!actions.canSend && (
          <p role="status">{m.widget_support_store_unavailable()}</p>
        )}
        {showFailure && failure && <p role="alert">{failureText}</p>}
        {content}
      </div>,
      undefined,
      m.widget_unavailable_title(),
    );
  if (
    confirmedUnsent &&
    failure &&
    ["support_email_unavailable", "support_email_not_configured"].includes(
      failure.error,
    ) &&
    ["draft", "failed"].includes(status) &&
    !card.payload.accepted_at
  ) {
    // 摘要只取本请求当前输入，不带聊天、邮件正文或私有订单快照。
    const summary = [
      m.widget_support_reference({ id: requestId }),
      REQUEST_TYPE_LABELS[requestTypes[fields.request_type]](),
      fields.description,
      fields.order_reference,
    ]
      .filter(Boolean)
      .join("\n");
    return wrap(
      <>
        <ContactUnavailable
          tokens={tokens}
          contact={{}}
          disabled={busy}
          description={
            failure.error === "support_email_not_configured"
              ? m.widget_support_send_not_configured()
              : undefined
          }
          onCopySummary={() => {
            void copy(summary).then((success) => {
              if (
                currentCard.current?.lifecycle !== "active" ||
                currentCard.current.payload.request_id !== requestId ||
                currentCard.current.payload.content_version !==
                  card.payload.content_version
              )
                return;
              setCopyFailed(!success);
              feedback?.show(
                success
                  ? m.widget_support_copy_success()
                  : m.widget_support_copy_failed(),
                success ? "default" : "danger",
              );
            });
          }}
        />
        {copyFailed && (
          <div>
            <p role="alert">{m.widget_support_copy_failed()}</p>
            <pre
              style={{
                // 手动复制是剪贴板失败时唯一的退路：长行要在窄面板里换行，不能被裁掉。
                maxWidth: "100%",
                overflowWrap: "anywhere",
                whiteSpace: "pre-wrap",
              }}
              tabIndex={0}
            >
              {summary}
            </pre>
          </div>
        )}
        <FormButton
          tokens={tokens}
          disabled={busy}
          variant="secondary"
          onClick={() => {
            setConfirmedUnsent(false);
            setEditingFailed(status === "failed");
            setFailure(undefined);
          }}
        >
          {m.widget_email_result_edit()}
        </FormButton>
      </>,
      false,
    );
  }
  const sendFailed =
    status === "failed" ||
    (failedOperation === "send" &&
      !!failure &&
      ![
        "invalid_fields",
        "stale_version",
        "support_email_unavailable",
        "support_email_not_configured",
      ].includes(failure.error));
  if (
    (sendFailed && !editingFailed) ||
    (busy && failedOperation === "send") ||
    (result && !(status === "failed" && editingFailed))
  ) {
    const accepted = ["accepted", "delivered", "bounced"].includes(status);
    return present(
      <>
        {failure && <p role="alert">{failureText}</p>}
        <RequestStatus
          tokens={tokens}
          titleId={titleId}
          state={
            accepted
              ? {
                  kind: sentInOverlay.current ? "accepted" : "already_sent",
                  receipt: {
                    requestId,
                    contactEmail: card.payload.contact_email,
                    time: result?.acceptedAt ?? result?.time,
                  },
                }
              : {
                  kind: sendFailed && !busy ? "send_failed" : "sending",
                }
          }
          onBack={onDismiss}
          onRetry={
            sendFailed && !busy && actions.canSend
              ? () => {
                  void send();
                }
              : undefined
          }
          onEdit={
            sendFailed && !busy
              ? () => {
                  setEditingFailed(true);
                }
              : undefined
          }
        />
      </>,
    );
  }
  if (review && card.payload.email && !edit && actions.canSend)
    return present(
      <ReviewCard
        disabled={busy}
        tokens={tokens}
        request={{
          preview: card.payload.email,
          requestType: REQUEST_TYPE_LABELS[requestTypes[fields.request_type]](),
        }}
        onSend={() => {
          void send();
        }}
        onEdit={() => {
          setReview(false);
        }}
        onNotNow={() => {
          void submit("pause");
        }}
      />,
      m.widget_review_title(),
    );
  const error = (field: string) =>
    failure?.fields?.includes(field)
      ? m.widget_support_field_error()
      : undefined;
  const fieldErrors = {
    request_type: error("request_type"),
    contact_email: error("contact_email"),
    description: error("description"),
    order_reference: error("order_reference"),
  };
  return present(
    <div ref={root} aria-busy={busy} style={ROOT_STYLE}>
      {!actions.canSend && (
        <p role="status">{m.widget_support_store_unavailable()}</p>
      )}
      {failure && <p role="alert">{failureText}</p>}
      {failure?.error === "stale_version" && (
        <FormButton
          tokens={tokens}
          onClick={() => {
            if (edit)
              actions.edit(
                requestId,
                card.payload.content_version,
                supportRequestFieldsSchema.strip().parse(card.payload),
              );
            setFailure(undefined);
          }}
        >
          {m.widget_support_latest()}
        </FormButton>
      )}
      <ContactCard
        tokens={tokens}
        requestType={requestTypes[fields.request_type]}
        email={fields.contact_email}
        description={fields.description}
        recipient={card.payload.to}
        orderReference={{
          value: fields.order_reference,
          source: fields.order_reference.trim() ? "entered" : undefined,
        }}
        fieldErrors={fieldErrors}
        onRequestTypeChange={(request_type) => {
          change({
            request_type: wireRequestType(request_type),
          });
        }}
        onEmailChange={(contact_email) => {
          change({ contact_email });
        }}
        onDescriptionChange={(description) => {
          change({ description });
        }}
        onOrderReferenceChange={(order_reference) => {
          change({ order_reference });
        }}
        draftActions={
          <>
            <FormButton
              tokens={tokens}
              disabled={busy || !actions.canSend || !card.payload.email}
              onClick={() => {
                void send();
              }}
            >
              {m.widget_contact_send()}
            </FormButton>
            <FormButton
              tokens={tokens}
              disabled={
                busy || !actions.canSend || !!edit || !card.payload.email
              }
              variant="secondary"
              onClick={() => {
                setReview(true);
              }}
            >
              {m.widget_contact_review()}
            </FormButton>
            <FormButton
              tokens={tokens}
              disabled={busy}
              variant="secondary"
              onClick={() => {
                void submit("save");
              }}
            >
              {m.widget_support_save()}
            </FormButton>
            <FormButton
              tokens={tokens}
              disabled={busy}
              variant="ghost"
              onClick={() => {
                void submit("pause");
              }}
            >
              {m.widget_contact_dismiss()}
            </FormButton>
          </>
        }
      />
    </div>,
    m.widget_contact_title(),
  );
}

/** open 失败没有卡；说明只表达当前不能打开表单，不表示已联系商家。 */
export function SupportOpenStatus({
  messageId,
  opening,
  tokens,
  titleId,
}: {
  titleId?: string;
  messageId: string;
  opening: {
    pending: boolean;
    ref?: string;
    orderReference?: string;
    error?: string;
  };
  tokens: WidgetTokens;
}) {
  const actions = useContext(SupportRequestContext);
  const unavailable = [
    "support_email_unavailable",
    "support_email_not_configured",
  ].includes(opening.error ?? "");
  if (opening.error === "expired" || opening.error === "not_accessible")
    return (
      <StatusCard
        tokens={tokens}
        icon="lock"
        title={
          opening.error === "expired"
            ? m.widget_support_expired_title()
            : m.widget_support_unavailable_title()
        }
        description={
          opening.error === "expired"
            ? m.widget_support_expired()
            : m.widget_support_inaccessible()
        }
      />
    );
  if (unavailable)
    return (
      <ContactUnavailable
        tokens={tokens}
        contact={{}}
        neutral
        title={m.widget_support_open_unavailable_title()}
        description={m.widget_support_open_unavailable()}
      />
    );
  return (
    <RequestStatus
      tokens={tokens}
      titleId={titleId}
      state={{ kind: opening.pending ? "loading" : "load_failed" }}
      onRetry={
        !opening.pending && actions?.open
          ? () => {
              void actions.open?.(
                messageId,
                opening.ref,
                opening.orderReference,
              );
            }
          : undefined
      }
    />
  );
}
